import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Cancel } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const CancelReasonsPage = () => {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Driver, 2 = Passenger

  useEffect(() => {
    fetchCancelReasons();
  }, []);

  const fetchCancelReasons = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getCancelReasons();

      if (response.success) {
        setReasons(response.data);
      } else {
        setError(response.message || 'Failed to load cancel reasons');
      }
    } catch (err) {
      console.error('Cancel reasons error:', err);
      setError('Failed to load cancel reasons');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedReason(null);
    setOpenDialog(true);
  };

  const handleEdit = (reason) => {
    setEditMode(true);
    setSelectedReason(reason);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cancel reason?')) {
      return;
    }

    try {
      const response = await apiService.deleteCancelReason(id);

      if (response.success) {
        alert('Cancel reason deleted successfully');
        fetchCancelReasons();
      } else {
        alert(response.message || 'Failed to delete cancel reason');
      }
    } catch (err) {
      alert('Failed to delete cancel reason');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;
      if (editMode) {
        response = await apiService.updateCancelReason(selectedReason.id, values);
      } else {
        response = await apiService.addCancelReason(values);
      }

      if (response.success) {
        alert(`Cancel reason ${editMode ? 'updated' : 'added'} successfully`);
        setOpenDialog(false);
        fetchCancelReasons();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Cancel reason operation error:', err);
      alert('Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    type: Yup.string().oneOf(['driver', 'passenger']).required('Type is required'),
  });

  const getFilteredReasons = () => {
    if (tabValue === 0) return reasons;
    if (tabValue === 1) return reasons.filter((r) => r.type === 'driver');
    if (tabValue === 2) return reasons.filter((r) => r.type === 'passenger');
    return reasons;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="error" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.type === 'driver' ? 'Driver' : 'Passenger'}
          color={params.row.type === 'driver' ? 'primary' : 'secondary'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'active' ? 'Active' : 'Inactive'}
          color={params.row.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Manage Cancel Reasons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage cancellation reasons for drivers and passengers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Cancel Reason
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All (${reasons.length})`} />
            <Tab label={`Driver (${reasons.filter((r) => r.type === 'driver').length})`} />
            <Tab label={`Passenger (${reasons.filter((r) => r.type === 'passenger').length})`} />
          </Tabs>

          <DataGrid
            rows={getFilteredReasons()}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Cancel Reason Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Cancel Reason</DialogTitle>
        <Formik
          initialValues={{
            title: selectedReason?.title || '',
            type: selectedReason?.type || '',
            status: selectedReason?.status || 'active',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    placeholder="Enter cancel reason title"
                  />

                  <FormControl fullWidth error={touched.type && Boolean(errors.type)}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Type"
                    >
                      <MenuItem value="driver">Driver</MenuItem>
                      <MenuItem value="passenger">Passenger</MenuItem>
                    </Select>
                    {touched.type && errors.type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.type}
                      </Typography>
                    )}
                  </FormControl>

                  {editMode && (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  {editMode ? 'Update' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default CancelReasonsPage;
